import {
  useFetchReadingsForExcludedConsumers,
  useGenerateBillsForExcludedConsumers,
} from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Calendar, CheckCircle, Download, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GeneratedBillsModal } from "./GeneratedBillsModal";
import { GeneratingBillsModal } from "./GeneratingBillsModal";

interface MeterReading {
  id: string;
  accountNo: string;
  consumerName: string;
  previousReading: number;
  currentReading: number;
  consumption: number;
  readingDate: string;
  status: "Actual" | "Estimated";
}

interface GeneratedBill {
  id: string;
  invoiceNo: string;
  consumerName: string;
  accountNumber: string;
  consumption: number;
  billAmount: number;
  outstanding: number;
  totalDue: number;
}

interface GenerateBillsModalProps {
  open: boolean;
  onClose: () => void;
  startDate: string;
  endDate: string;
  selectedCount: number;
  onGenerateBills?: (readings: MeterReading[]) => void;
}

const addDays = (dateStr: string, days: number) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

export const GenerateBillsModal = ({
  open,
  onClose,
  startDate,
  endDate,
  selectedCount,
  onGenerateBills,
}: GenerateBillsModalProps) => {
  const { id } = useParams();

  const [billingEndDate, setBillingEndDate] = useState(endDate);
  const [isExtended, setIsExtended] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasfetched, setHasFetched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  console.log("Selected Count:", billingEndDate, endDate);
  const { data: meterReadings } = useFetchReadingsForExcludedConsumers(id);
  useEffect(() => {
    setBillingEndDate(endDate);
  }, [endDate, startDate]);
  const generateBills = useGenerateBillsForExcludedConsumers(id);
  // New states for modal flow
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [showGeneratedModal, setShowGeneratedModal] = useState(false);
  const [generatedBills, setGeneratedBills] = useState<GeneratedBill[]>([]);
  const handleExtend = () => {
    if (isExtended) return;
    setBillingEndDate(addDays(billingEndDate, 5));
    setIsExtended(true);
  };

  const handleFetchReadings = async () => {
    setHasFetched(true);
  };

  const handleGenerateBills = async () => {
    // Show generating modal
    setShowGeneratingModal(true);

    try {
      // Call mock API to generate bills
      const bills = await generateBills.mutateAsync(id);
      setGeneratedBills(bills);

      setShowGeneratingModal(false);
      setShowGeneratedModal(true);
    } catch (error) {
      setShowGeneratingModal(false);
      // Handle error - show error toast/message
    }
  };

  const handleGenerationComplete = () => {
    // This will be called from GeneratingBillsModal when progress completes
    setShowGeneratingModal(false);
    setShowGeneratedModal(true);
  };

  const handleApproveBills = (bills: GeneratedBill[]) => {
    console.log("Bills approved:", bills);
    // Call final API to approve/finalize bills
    // Show success message
    // Close all modals
    setShowGeneratedModal(false);
    onClose();

    if (onGenerateBills) {
      onGenerateBills(meterReadings);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCloseAll = () => {
    setShowGeneratingModal(false);
    setShowGeneratedModal(false);
    onClose();
  };

  // Status badge configuration
  const getStatusBadgeConfig = (status: string) => {
    switch (status) {
      case "Actual":
        return {
          variant: "default" as const,
          className: "bg-green-100 text-green-800 border-green-200",
        };
      case "Estimated":
        return {
          variant: "secondary" as const,
          className: "bg-blue-100 text-blue-800 border-blue-200",
        };
      default:
        return {
          variant: "outline" as const,
          className: "bg-gray-100 text-gray-800",
        };
    }
  };

  // Define columns for the table
  const columns: TableColumn<MeterReading>[] = [
    {
      key: "accountNo",
      header: "Account No",
      type: "text",
      sortable: false,
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
      sortable: false,
    },
    {
      key: "previousReading",
      header: "Previous Reading",
      type: "text",
      sortable: false,
    },
    {
      key: "currentReading",
      header: "Current Reading",
      type: "text",
      sortable: false,
    },
    {
      key: "consumption",
      header: "Consumption",
      type: "custom",
      sortable: false,
      render: (value: number) => `${value} kWh`,
    },
    {
      key: "readingDate",
      header: "Reading Date",
      type: "text",
      sortable: false,
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      sortable: false,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  // Calculate pagination
  const totalItems = meterReadings?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  if (!open) return null;

  return (
    <>
      {/* Main Generate Bills Modal */}
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
          <div className="sticky top-0 bg-white border-b z-10 px-6 py-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Generate Bills - Billing Period
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Review billing period and fetch meter readings for selected
                  consumers
                </p>
              </div>
              <button
                onClick={onClose}
                className="hover:bg-gray-100 p-1 rounded"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4">
            {/* Billing Period Card */}
            <div className="border rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Billing Period
              </h3>

              <div className="grid grid-cols-2 gap-6 mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Start Date</p>
                  <p className="font-medium">{startDate}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">End Date</p>
                  <p className="font-medium">{billingEndDate}</p>
                </div>
              </div>

              {/* Info Banner */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800 mb-4">
                You can extend the billing period by 5 days to allow more time
                for meter reading collection.
              </div>

              <button
                disabled={isExtended}
                onClick={handleExtend}
                className={`w-full flex items-center justify-center gap-2 border rounded-md py-2 text-sm font-medium transition
                ${
                  isExtended
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }
              `}
              >
                <Calendar className="h-4 w-4" />
                {isExtended
                  ? "Period Extended by 5 Days"
                  : "Extend Billing Period by 5 Days"}
              </button>
            </div>

            {/* Meter Readings Section */}
            {!hasfetched ? (
              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  Meter Readings
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Fetch meter readings for {selectedCount} selected consumers.
                </p>

                <Button onClick={handleFetchReadings} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Fetch Meter Readings
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Success Banner */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-800 font-medium">
                    Successfully fetched meter readings for {selectedCount}{" "}
                    consumers
                  </p>
                </div>

                {/* Meter Readings Table */}
                <div className="border rounded-lg">
                  <div className="px-4 py-3 border-b bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Meter Readings
                    </h3>
                  </div>
                  <div className="p-4">
                    <GenericTable
                      data={meterReadings?.result || []}
                      columns={columns}
                      rowKey="id"
                      loading={false}
                      emptyMessage="No meter readings found"
                      pagination={{
                        currentPage,
                        totalPages,
                        totalItems,
                        itemsPerPage: PAGE_SIZE,
                        startItem: startItem + 1,
                        endItem,
                      }}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4">
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleGenerateBills}
                disabled={!hasfetched}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Generate Bills
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Generating Bills Modal */}
      <GeneratingBillsModal
        open={showGeneratingModal}
        onClose={() => setShowGeneratingModal(false)}
        consumerCount={selectedCount}
        onComplete={handleGenerationComplete}
        simulateProgress={false} // Set to false since we're using real API
      />

      {/* Generated Bills Modal */}
      <GeneratedBillsModal
        open={showGeneratedModal}
        onClose={handleCloseAll}
        bills={generatedBills}
        onApprove={handleApproveBills}
      />
    </>
  );
};
